"use strict";
//
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.StorageLimitError = void 0;
exports.uploadXhr = uploadXhr;
exports.uploadMultipart = uploadMultipart;
exports.retryStage = retryStage;
const core_1 = require("@hcengineering/core");
const retry_1 = require("@hcengineering/retry");
/** Upload rejected because the workspace storage/plan limit is reached (HTTP 413). @public */
class StorageLimitError extends Error {
    isStorageLimit = true;
    constructor(message) {
        super(message);
        this.name = 'StorageLimitError';
    }
}
exports.StorageLimitError = StorageLimitError;
/** Extract the server's JSON `message`, falling back to a generic limit text. */
function serverMessage(body) {
    try {
        const m = JSON.parse(body)?.message;
        if (typeof m === 'string' && m.length > 0)
            return m;
    }
    catch {
        /* not JSON */
    }
    return 'Resource limit exceeded';
}
/** @public */
async function uploadXhr(upload, options) {
    const signal = options?.signal;
    const onProgress = options?.onProgress;
    // Check if already aborted before starting
    if (signal?.aborted === true) {
        throw new Error('Upload aborted');
    }
    const xhr = new XMLHttpRequest();
    const abortHandler = () => {
        xhr.abort();
    };
    if (signal !== undefined) {
        signal.addEventListener('abort', abortHandler);
    }
    try {
        return await new Promise((resolve, reject) => {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    onProgress?.({
                        loaded: event.loaded,
                        total: event.total,
                        percentage: Math.round((100 * event.loaded) / event.total)
                    });
                }
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve({
                        status: xhr.status,
                        responseText: xhr.responseText
                    });
                }
                else if (xhr.status === 413) {
                    // Storage/plan limit reached — surface the server's message, not the generic HTTP text.
                    reject(new StorageLimitError(serverMessage(xhr.responseText)));
                }
                else {
                    reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                }
            };
            xhr.onerror = () => {
                reject(new Error('Network error'));
            };
            xhr.onabort = () => {
                reject(new Error('Upload aborted'));
            };
            xhr.ontimeout = () => {
                reject(new Error('Upload timeout'));
            };
            xhr.open(upload.method, upload.url, true);
            for (const key in upload.headers) {
                xhr.setRequestHeader(key, upload.headers[key]);
            }
            xhr.send(upload.body);
        });
    }
    finally {
        if (signal !== undefined) {
            signal.removeEventListener('abort', abortHandler);
        }
    }
}
/** @public */
async function uploadMultipart(upload, options) {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const { url, headers, body } = upload;
    const signal = options?.signal;
    const onProgress = options?.onProgress;
    let uploadId;
    try {
        throwIfAborted(signal);
        // Retry each stage on transient errors. Create yields a fresh uploadId per attempt;
        // part/complete reuse the current uploadId and are idempotent by partNumber.
        uploadId = (await retryStage(signal, async () => await multipartUploadCreate(url, { ...headers, 'Content-Type': body.type }, signal))).uploadId;
        const parts = [];
        const totalParts = Math.ceil(body.size / CHUNK_SIZE);
        let uploaded = 0;
        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
            const start = (partNumber - 1) * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, body.size);
            const chunk = body.slice(start, end);
            throwIfAborted(signal);
            const partOptions = {
                signal,
                onProgress: onProgress !== undefined
                    ? (progress) => {
                        const loaded = uploaded + progress.loaded;
                        onProgress({
                            loaded,
                            total: body.size,
                            percentage: Math.round((loaded * 100) / body.size)
                        });
                    }
                    : undefined
            };
            const currentUploadId = uploadId;
            const { etag } = await retryStage(signal, async () => await multipartUploadPart(url, headers, currentUploadId, partNumber, chunk, partOptions));
            parts.push({ partNumber, etag });
            uploaded += chunk.size;
        }
        throwIfAborted(signal);
        const currentUploadId = uploadId;
        await retryStage(signal, async () => {
            await multipartUploadComplete(url, headers, currentUploadId, parts, signal);
        });
    }
    catch (err) {
        if (uploadId !== undefined) {
            await multipartUploadAbort(url, headers, uploadId);
        }
        throw err instanceof Error ? err : new Error(String(err));
    }
}
function throwIfAborted(signal) {
    if (signal?.aborted === true) {
        throw new Error('Upload aborted');
    }
}
// Retry a multipart stage. Does not retry on abort or non-retriable client errors (4xx except 408/429).
async function retryStage(signal, op) {
    const isRetryable = (err) => {
        if (signal?.aborted === true)
            return false;
        const message = err instanceof Error ? err.message : String(err);
        const m = /status (\d{3})/.exec(message);
        if (m != null) {
            const status = parseInt(m[1], 10);
            if (status >= 400 && status < 500 && status !== 408 && status !== 429)
                return false;
        }
        return true;
    };
    return await (0, retry_1.withRetry)(op, {
        maxRetries: 5,
        isRetryable,
        delayStrategy: retry_1.DelayStrategyFactory.exponentialBackoff({
            initialDelayMs: 200,
            maxDelayMs: 5000,
            backoffFactor: 2,
            jitter: 0.2
        })
    });
}
async function multipartUploadCreate(baseUrl, headers, signal) {
    const response = await fetch(baseUrl, {
        signal,
        method: 'POST',
        headers
    });
    if (!response.ok) {
        if (response.status === 413) {
            throw new StorageLimitError(serverMessage(await response.text().catch(() => '')));
        }
        // Keep "(status NNN)" — retryStage.isRetryable parses it to skip retrying client 4xx.
        throw new Error(`Failed to initialize multipart upload (status ${response.status})`);
    }
    const { uuid, uploadId } = await response.json();
    return { uuid, uploadId };
}
async function multipartUploadComplete(baseUrl, headers, uploadId, parts, signal) {
    const url = new URL((0, core_1.concatLink)(baseUrl, '/complete'));
    url.searchParams.set('uploadId', uploadId);
    const response = await fetch(url, {
        signal,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers
        },
        body: JSON.stringify({ parts })
    });
    if (!response.ok) {
        if (response.status === 413) {
            throw new StorageLimitError(serverMessage(await response.text().catch(() => '')));
        }
        throw new Error(`Failed to complete multipart upload (status ${response.status})`);
    }
}
async function multipartUploadPart(baseUrl, headers, uploadId, partNumber, blob, options) {
    const url = new URL((0, core_1.concatLink)(baseUrl, '/part'));
    url.searchParams.set('uploadId', uploadId);
    url.searchParams.set('partNumber', `${partNumber}`);
    const result = await uploadXhr({
        url: url.toString(),
        method: 'PUT',
        headers,
        body: blob
    }, options);
    try {
        const response = JSON.parse(result.responseText);
        return { etag: response.etag };
    }
    catch (err) {
        throw new Error(`Failed to parse response for part ${partNumber}`);
    }
}
async function multipartUploadAbort(baseUrl, headers, uploadId) {
    const url = new URL((0, core_1.concatLink)(baseUrl, '/abort'));
    url.searchParams.set('uploadId', uploadId);
    const response = await fetch(url, {
        method: 'POST',
        headers
    });
    if (!response.ok) {
        throw new Error('Failed to reject multipart upload');
    }
}
//# sourceMappingURL=upload.js.map